import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Col,
  Form,
  Input,
  Row,
  Spin,
  Tabs as Tab,
  Button,
  Tooltip,
  Typography,
  Divider,
  Select,
  notification,
} from 'antd'
import { CopyOutlined } from '@ant-design/icons'

import PageLayout from '../../layouts/DetailPageLayout'
import Table from '../../components/Tables/SearchTable'
import Tabs from '../../components/Tabs'
import { wombaOptions } from '../../../data/selectOptions'

import {
  getInsuranceCompanies,
  selectAllInsuranceCompanies,
  resetState as resetInsuranceCompaniesState,
} from '../../../state/modules/insuranceCompanies'
import {
  getAgencies,
  selectAllAgencies,
  resetState as resetAgenciesState,
} from '../../../state/modules/agencies'
import useDispatchHttp from '../../../hooks/dispatchHttpHandler'

import { INSURANCE_COMPANY, AGENCIES } from '../../../constants'
import useDebounce from '../../../hooks/useDebounce'
import { useUser } from '../../../providers/UserProvider'
import { validateSearchParams } from '../../../utils/functions'

const Organizations = () => {
  const { isUserActionAllowed } = useUser()
  const dispatchHttp = useDispatchHttp()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [form] = Form.useForm()

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab')
    return tab ? parseInt(tab, 10) : 0
  }

  const [activeTab, setActiveTab] = useState(getInitialTab())

  const [searchParams, setSearchParams] = useState({})
  const insuranceCompaniesList = useSelector(selectAllInsuranceCompanies)
  const agenciesList = useSelector(selectAllAgencies)
  const { count: insuranceCompaniesCount } = useSelector(
    state => state.insuranceCompanies
  )
  const { count: agencyCount } = useSelector(state => state.agencies)
  const loadingStatus = useSelector(state => state.insuranceCompanies.status)

  const tabs = [
    {
      name: 'Insurance Companies',
      key: 0,
      getData: getInsuranceCompanies,
      count: insuranceCompaniesCount,
      totalData: insuranceCompaniesList,
      entity: INSURANCE_COMPANY,
      permission: 'view_insurancecompany',
    },
    {
      name: 'Agencies',
      key: 1,
      getData: getAgencies,
      count: agencyCount,
      totalData: agenciesList,
      entity: AGENCIES,
      permission: 'view_agency',
    },
  ]
  const allowedTabs = tabs
    .filter(item => isUserActionAllowed(item.permission) || !item.permission)
    .map((item, index) => {
      item.key = index
      return item
    })

  const handleChangeSearch = useDebounce(e => {
    const { value } = e.target
    setSearchParams({ ...searchParams, search: value.trim() })
    dispatchHttp(
      allowedTabs[activeTab]?.getData({
        ...validateSearchParams(searchParams),
        search: value.trim(),
      })
    )
  }, 500)

  const handleChangeWombaFilter = state => {
    setSearchParams({ ...searchParams, womba: state })

    dispatchHttp(
      allowedTabs[activeTab]?.getData({
        ...searchParams,
        ...validateSearchParams({ womba: state }),
      })
    )
  }

  const renderRetrieverId = text =>
    text ? text.cms_id : <Typography.Text type="secondary">No info</Typography.Text>

  const renderRetrieverColumn = text => (
    <>
      {(() => {
        if (!text?.retriever) {
          return <Typography.Text type="secondary">No info</Typography.Text>
        }

        return (
          <>
            {text?.retriever?.fullname ? (
              <span>
                {text?.retriever?.fullname}
                <Divider type="vertical" className="organization-name__divider" />
              </span>
            ) : null}
            {text?.retriever?.contname}
          </>
        )
      })()}
    </>
  )

  const handleCopyToClipboard = text => {
    if (text) {
      navigator.clipboard.writeText(text)
      notification.success({
        message: 'ID Copied!',
        duration: 2,
      })
    }
  }

  const organizationsTableColumns = [
    {
      title: 'Name',
      key: 'name',
      width: 280,
    },
    {
      title: 'Department',
      key: 'department',
      align: 'center',
      width: 150,
    },
    {
      title: 'CMS ID',
      key: 'cms_id',
      align: 'center',
      width: 100,
      render: text => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ width: '100%', textAlign: 'center' }}>{text}</div>
          {text && (
            <Tooltip title="Copy ID">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined style={{ color: '#1890ff' }} />}
                onClick={e => {
                  e.stopPropagation()
                  handleCopyToClipboard(text)
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Womba',
      key: 'womba',
      align: 'center',
      width: 100,
      render: text => {
        const wombaOption = wombaOptions.find(option => option.value === text)
        return wombaOption ? wombaOption.label : text
      },
    },
    {
      title: 'Retriever CMS ID',
      key: 'retriever',
      align: 'center',
      width: 100,
      render: text => renderRetrieverId(text),
    },
    {
      title: 'Retriever',
      key: 'retriever',
      align: 'center',
      width: 170,
      render: (_, col, text) => renderRetrieverColumn(text),
    },
    {
      title: 'APS CMS Supervisor ID',
      key: 'cms_aps_supervisor_id',
      align: 'center',
      width: 100,
    },
    {
      title: 'ODR Supervisor CMS ID',
      key: 'cms_odr_supervisor_id',
      align: 'center',
      width: 100,
    },
    {
      title: 'DS Enabled',
      key: 'is_active_dump_screen',
      align: 'center',
      width: 60,
      render: text => (text ? 'Yes' : 'No'),
    },
  ]

  useEffect(() => {
    if (!allowedTabs[activeTab]) return
    form?.resetFields()
    dispatchHttp(allowedTabs[activeTab]?.getData({}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchHttp, activeTab])

  useEffect(
    () => () => {
      dispatch(resetInsuranceCompaniesState())
      dispatch(resetAgenciesState())
    },
    [dispatch]
  )

  return (
    <Spin spinning={false}>
      <PageLayout
        title={
          <>
            Organizations
            <span className="badge">{allowedTabs[activeTab]?.count}</span>
          </>
        }
      >
        <Tabs
          fullWidth
          theme="dark"
          tabsProps={{
            size: 'small',
            tabBarGutter: 24,
            onChange: key => {
              setSearchParams({
                ...searchParams,
                search: '',
                womba: 'all',
              })
              setActiveTab(+key)
            },
            activeKey: `${activeTab}`,
          }}
        >
          {allowedTabs.map(({ key, name }) => (
            <Tab.TabPane tab={name} key={key}>
              <Row
                gutter={[16, 32]}
                style={{
                  background: '#fff',
                  padding: '14px 12px',
                  justifyContent: 'flex-end',
                  margin: 0,
                }}
              >
                <Form
                  form={form}
                  layout="inline"
                  className="sort-form"
                  component="div"
                  initialValues={{ search: '', womba: 'all' }}
                  size="small"
                >
                  <Row>
                    <Col>
                      <Form.Item label="Womba" name="womba" size="small">
                        <Select style={{ width: 150 }} onChange={handleChangeWombaFilter}>
                          <Select.Option value="all">All</Select.Option>
                          {wombaOptions.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item label="Search" name="search" size="small">
                        <Input
                          onChange={handleChangeSearch}
                          type="text"
                          className="search-input"
                          placeholder="Name, Department, CMS ID or Retriever CMS ID"
                          style={{ width: 300 }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Row>
              <Table
                columns={organizationsTableColumns}
                onLoadData={allowedTabs[activeTab]?.getData}
                formData={validateSearchParams(searchParams)}
                totalCount={allowedTabs[activeTab]?.count}
                totalData={allowedTabs[activeTab]?.totalData}
                loadingStatus={loadingStatus}
                entity={allowedTabs[activeTab]?.entity}
                onRow={record => ({
                  onClick: () => {
                    const type = allowedTabs[activeTab].entity
                    history.push(`/organizations/${type}/${record.id}`)
                  },
                  style: { cursor: 'pointer' },
                })}
              />
            </Tab.TabPane>
          ))}
        </Tabs>
      </PageLayout>
    </Spin>
  )
}

export default Organizations
